/**
 * Produktionsabschnitt im Flotten-Tab (unten links).
 */
import { getShipBuildCost, SHIP_BUILD_TIMES } from '../config/ShipsConfig';
import { formatNumber, formatFull } from '../math/formatNumber';
import { formatTime } from './BuildingCardTimer';

const WRAP_STYLE =
  'background:rgba(0,0,0,0.55);backdrop-filter:blur(10px);' +
  'border:1px solid rgba(136,204,255,0.35);border-radius:10px;' +
  'padding:clamp(10px,2vw,16px);font-family:monospace;color:#8cf;' +
  'min-width:clamp(140px,25vw,200px);' +
  'box-shadow:0 4px 20px rgba(0,0,0,0.4);';

const BTN_STYLE =
  'margin-top:12px;padding:12px 16px;width:100%;min-height:44px;' +
  'background:linear-gradient(180deg,rgba(136,204,255,0.25) 0%,rgba(100,180,255,0.15) 100%);' +
  'border:1px solid rgba(136,204,255,0.5);border-radius:8px;color:#fff;' +
  'cursor:pointer;font-family:monospace;font-size:clamp(11px,2.6vw,13px);font-weight:600;' +
  'text-shadow:0 0 8px rgba(136,204,255,0.5);transition:all 0.15s;';

const BTN_DISABLED = 'opacity:0.5;cursor:not-allowed;pointer-events:none;';

export interface FleetTabProductionCallbacks {
  onBuild?: (shipType: number) => void;
}

export function createFleetTabProduction(
  shipType: number,
  count: number,
  canBuild: boolean,
  resources: { feronium: number; chrysalium: number; neurogel: number },
  cb?: FleetTabProductionCallbacks,
): HTMLDivElement {
  const wrap = document.createElement('div');
  wrap.style.cssText = WRAP_STYLE;

  wrap.innerHTML =
    `<div style="color:#6a9aaa;font-size:10px;letter-spacing:0.08em;text-transform:uppercase;margin-bottom:4px;">Bestand</div>` +
    `<div style="color:#fff;font-size:clamp(1rem,4vw,1.375rem);font-weight:700;text-shadow:0 0 12px rgba(136,204,255,0.4);margin-bottom:10px;">${count}</div>` +
    `<div style="color:#6a8a9a;font-size:10px;letter-spacing:0.08em;text-transform:uppercase;margin-bottom:4px;">Kosten</div>`;

  const cost = getShipBuildCost(shipType);
  const canAfford = resources.feronium >= cost.feronium &&
    resources.chrysalium >= cost.chrysalium &&
    resources.neurogel >= cost.neurogel;

  const costTip = `${formatFull(cost.feronium)}F  ${formatFull(cost.chrysalium)}C  ${formatFull(cost.neurogel)}N`;
  const costStr = `${formatNumber(cost.feronium)}F ${formatNumber(cost.chrysalium)}C ${formatNumber(cost.neurogel)}N`;
  wrap.innerHTML += `<div style="color:#bdf;font-size:14px;font-weight:500;" title="${costTip}">${costStr}</div>`;

  const buildTimeSec = SHIP_BUILD_TIMES[shipType] ?? 0;
  const timeStr = formatTime(buildTimeSec);

  const btn = document.createElement('button');
  btn.style.cssText = BTN_STYLE;
  if (!canBuild || !canAfford) btn.style.cssText += BTN_DISABLED;
  btn.innerHTML = `\u25B8 Bauen <span style="opacity:0.6;font-size:0.85em;">\u23F1${timeStr}</span>`;
  btn.addEventListener('click', () => {
    if (!canBuild || !canAfford) return;
    btn.disabled = true;
    btn.style.opacity = '0.5';
    btn.textContent = '\u2692 Bau\u2026';
    cb?.onBuild?.(shipType);
  });
  wrap.appendChild(btn);

  return wrap;
}

export function updateFleetTabProduction(
  wrap: HTMLDivElement,
  shipType: number,
  count: number,
  canBuild: boolean,
  resources: { feronium: number; chrysalium: number; neurogel: number },
  cb?: FleetTabProductionCallbacks,
): void {
  const cost = getShipBuildCost(shipType);
  const canAfford = resources.feronium >= cost.feronium &&
    resources.chrysalium >= cost.chrysalium &&
    resources.neurogel >= cost.neurogel;

  const costTip = `${formatFull(cost.feronium)}F  ${formatFull(cost.chrysalium)}C  ${formatFull(cost.neurogel)}N`;
  const costStr = `${formatNumber(cost.feronium)}F ${formatNumber(cost.chrysalium)}C ${formatNumber(cost.neurogel)}N`;

  wrap.innerHTML = '';
  wrap.innerHTML +=
    `<div style="color:#6a9aaa;font-size:10px;letter-spacing:0.08em;text-transform:uppercase;margin-bottom:4px;">Bestand</div>` +
    `<div style="color:#fff;font-size:clamp(1rem,4vw,1.375rem);font-weight:700;text-shadow:0 0 12px rgba(136,204,255,0.4);margin-bottom:10px;">${count}</div>` +
    `<div style="color:#6a8a9a;font-size:10px;letter-spacing:0.08em;text-transform:uppercase;margin-bottom:4px;">Kosten</div>` +
    `<div style="color:#bdf;font-size:14px;font-weight:500;" title="${costTip}">${costStr}</div>`;

  const buildTimeSec2 = SHIP_BUILD_TIMES[shipType] ?? 0;
  const timeStr2 = formatTime(buildTimeSec2);

  const btn = document.createElement('button');
  btn.style.cssText = BTN_STYLE;
  if (!canBuild || !canAfford) btn.style.cssText += BTN_DISABLED;
  btn.innerHTML = `\u25B8 Bauen <span style="opacity:0.6;font-size:0.85em;">\u23F1${timeStr2}</span>`;
  btn.addEventListener('click', () => {
    if (!canBuild || !canAfford) return;
    btn.disabled = true;
    btn.style.opacity = '0.5';
    btn.textContent = '\u2692 Bau\u2026';
    cb?.onBuild?.(shipType);
  });
  wrap.appendChild(btn);
}
